import * as Sentry from '@sentry/react';
import { getGraphQLClient, LOG_JS_ERROR } from './graphql';

/**
 * Logs error to our database via GraphQL (fire and forget)
 */
function logToDatabase(error: unknown, context?: Record<string, unknown>) {
  try {
    const errorObj = error instanceof Error ? error : new Error(String(error));
    const client = getGraphQLClient();

    client
      .request(LOG_JS_ERROR, {
        errorClass: errorObj.name || 'Error',
        message: errorObj.message || String(error),
        stack: errorObj.stack,
        url: window.location.href,
        userAgent: navigator.userAgent,
        context: context || null,
      })
      .catch(() => {
        // Silently fail - don't want logging errors to cause more errors
      });
  } catch {
    // Silently fail
  }
}

/**
 * Captures an exception to Sentry with optional context
 * @param error - The error to capture
 * @param context - Additional context to attach to the error
 */
export function captureException(error: unknown, context?: Record<string, unknown>) {
  // Always log to console for development visibility
  console.error(error);

  // Log to our database in production/staging (PROD is true when NODE_ENV=production)
  if (import.meta.env.PROD) {
    logToDatabase(error, context);
  }

  // Only send to Sentry in production/staging environments
  if (import.meta.env.PROD) {
    if (context) {
      Sentry.withScope((scope) => {
        Object.entries(context).forEach(([key, value]) => {
          // Set tags for primitive values and arrays, context for objects
          if (
            value === null ||
            value === undefined ||
            typeof value !== 'object' ||
            Array.isArray(value)
          ) {
            scope.setTag(key, String(value));
          } else {
            scope.setContext(key, value as Record<string, unknown>);
          }
        });
        Sentry.captureException(error);
      });
    } else {
      Sentry.captureException(error);
    }
  }
}

/**
 * Captures a message to Sentry with optional context
 * @param message - The message to capture
 * @param level - The severity level (default: 'error')
 * @param context - Additional context to attach to the message
 */
export function captureMessage(
  message: string,
  level: Sentry.SeverityLevel = 'error',
  context?: Record<string, unknown>
) {
  console.error(message);

  if (import.meta.env.PROD) {
    // Log to our database
    logToDatabase(new Error(message), { ...context, level });

    if (context) {
      Sentry.withScope((scope) => {
        Object.entries(context).forEach(([key, value]) => {
          // Set tags for primitive values and arrays, context for objects
          if (
            value === null ||
            value === undefined ||
            typeof value !== 'object' ||
            Array.isArray(value)
          ) {
            scope.setTag(key, String(value));
          } else {
            scope.setContext(key, value as Record<string, unknown>);
          }
        });
        Sentry.captureMessage(message, level);
      });
    } else {
      Sentry.captureMessage(message, level);
    }
  }
}
